import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatTime } from '../../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Calendar, BookOpen, ClipboardList, FileText, Bell,
  Clock, MapPin, Video, ChevronRight, Award, AlertCircle, Loader2, Sparkles
} from 'lucide-react';
import InstantRewardDialog from '../../components/instructor/InstantRewardDialog';

export default function InstructorDashboardPage() {
  const { api } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [selectedSectionForReward, setSelectedSectionForReward] = useState(null);
  const [sectionStudents, setSectionStudents] = useState([]);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/instructor/dashboard');
      setDashboard(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const openRewardDialog = async (section) => {
    setSelectedSectionForReward(section);
    
    // Fetch students for this section
    try {
      const res = await api.get('/enrollments/', { params: { section_id: section.id } });
      const enrollments = res.data?.results || res.data || [];
      const students = enrollments.map(e => {
        // learner_name is "First Last" format
        const nameParts = (e.learner_name || '').split(' ');
        return {
          id: e.learner, // learner is the learner_id
          first_name: nameParts[0] || 'Student',
          last_name: nameParts.slice(1).join(' ') || ''
        };
      });
      setSectionStudents(students);
    } catch (error) {
      console.error('Failed to fetch students:', error);
      setSectionStudents([]);
    }
    
    setRewardDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Instructor Dashboard</h1>
          <p className="text-muted-foreground">Manage your classes and students</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <BookOpen className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{dashboard?.sections?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Active Classes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{dashboard?.total_students || 0}</p>
                <p className="text-sm text-muted-foreground">Total Students</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <ClipboardList className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{dashboard?.pending_grades || 0}</p>
                <p className="text-sm text-muted-foreground">Pending Grades</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <FileText className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{dashboard?.recent_notes?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Recent Notes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Grades Alert */}
      {dashboard?.pending_grades > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <span className="text-amber-800">
                  You have <strong>{dashboard.pending_grades}</strong> submissions awaiting grades
                </span>
              </div>
              <Link to="/instructor/assignments">
                <Button variant="outline" size="sm">
                  Grade Now
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Classes Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            My Classes
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard?.sections?.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No active classes assigned</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dashboard?.sections?.map(section => (
                <Card key={section.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold">{section.course_title}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          {section.delivery_type === 'online' ? (
                            <><Video className="h-4 w-4" /> Online</>
                          ) : (
                            <><MapPin className="h-4 w-4" /> {section.location}</>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline">
                        {section.enrolled_count}/{section.capacity}
                      </Badge>
                    </div>
                    <div className="text-sm space-y-1 mb-4">
                      <p className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {section.start_date} - {section.end_date}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {Array.isArray(section.meeting_days) ? section.meeting_days.join(', ') : (section.meeting_days || 'TBD')} • {formatTime(section.start_time)} - {formatTime(section.end_time)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Link to={`/instructor/roster?section_id=${section.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full">
                          <Users className="h-4 w-4 mr-1" />
                          Roster
                        </Button>
                      </Link>
                      <Link to={`/instructor/attendance?section_id=${section.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full">
                          <ClipboardList className="h-4 w-4 mr-1" />
                          Attendance
                        </Button>
                      </Link>
                      <Button 
                        size="sm" 
                        className="bg-yellow-500 hover:bg-yellow-600 text-white flex-1"
                        onClick={() => openRewardDialog(section)}
                        data-testid={`award-points-btn-${section.id}`}
                      >
                        <Sparkles className="h-4 w-4 mr-1" />
                        Award
                      </Button>
                    </div>
                    {section.meeting_link && (
                      <a 
                        href={section.meeting_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block mt-2"
                      >
                        <Button size="sm" className="w-full">
                          <Video className="h-4 w-4 mr-1" />
                          Join Meeting
                        </Button>
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Notes */}
      {dashboard?.recent_notes?.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Recent Notes
              </CardTitle>
              <Link to="/instructor/notes">
                <Button variant="ghost" size="sm">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.recent_notes.map(note => (
                <div key={note.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                  <FileText className="h-4 w-4 text-muted-foreground mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{note.learner_name}</span>
                      <Badge variant="secondary" className="text-xs">{note.note_type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{note.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(note.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instant Reward Dialog */}
      <InstantRewardDialog
        open={rewardDialogOpen}
        onOpenChange={setRewardDialogOpen}
        sectionId={selectedSectionForReward?.id}
        sessionNumber={1}
        students={sectionStudents}
        onRewardGiven={(data) => {
          toast.success(`Awarded ${data.amount} points!`);
        }}
      />
    </div>
  );
}
